import React from 'react'
import { Link, useNavigate } from 'react-router-dom'
import Helmet from 'react-helmet'
import styled from 'styled-components'
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar'
import 'react-circular-progressbar/dist/styles.css'
import { getSkipRouteStorage } from '../../utils'

import AbsoluteFill from '../Layout/AbsoluteFill'
import breakpoints from '../../constants/breakpoints'
import CloseAppButton from '../CloseAppButton'
import TopHeader from '../TopHeader'

import { Slide as SlideType, ThemeProps } from '../../types'
import GoBackLink from '../GoBackLink'

interface Props extends ThemeProps {
  activeRouteIndex: number
  baseName: string
  modalIsOpen: boolean
  onModalButtonClick: () => void
  routeIsIntroduction: boolean
  routes: SlideType[]
}

const CHAPTER_WIDTH = 38
const CHAPTER_HEIGHT = 30

const Header: React.FC<Props> = ({
  activeRouteIndex,
  baseName,
  modalIsOpen,
  onModalButtonClick,
  routeIsIntroduction,
  routes,
  theme,
}) => {
  const history = useNavigate()
  const filteredRoutes = routes.filter((route) => route.type !== 'intro')
  const routesPassed = filteredRoutes.slice(0, activeRouteIndex)
  const chapterSaved = localStorage.getItem('chapter')
  let progress = Math.round((routesPassed.length / filteredRoutes.length) * 100)
  let metaTitle

  const chapterRoutes = filteredRoutes.filter((route) => route.type === 'chapter')
  let isActiveChapter = routes
    .slice(0, activeRouteIndex + 1)
    .filter((route) => route.type === 'chapter').length
  if (isActiveChapter === 0) isActiveChapter = 1

  switch (activeRouteIndex) {
    case -1:
      // Leave status on 100% when navigating to /print-overview.
      progress = 100
      isActiveChapter = chapterRoutes.length
      metaTitle = `Keuzehulpen ${baseName} - Print overzicht`
      break
    case 0:
      metaTitle = `Keuzehulpen ${baseName} - ${routes[activeRouteIndex].title}`
      break
    default:
      metaTitle = `Keuzehulpen ${baseName} - Stap ${isActiveChapter} - ${routes[activeRouteIndex].title}`
  }

  const isSkippedChapter = (chapterNumber: number) => {
    const skipChapters = getSkipRouteStorage()

    return skipChapters && skipChapters.length > 0
      ? skipChapters.some((chapter) => chapter === chapterNumber.toString())
      : false
  }

  return (
    <>
      <TopHeader />

      {activeRouteIndex !== 0 && activeRouteIndex !== -1 && (
        <StyledHeader id="progress-header">
          <Background />
          <Helmet title={metaTitle} />

          <LeftSide>
            <Brand>
              <LogoWrapper>
                <AbsoluteFill>
                  <CircularProgressbar
                    value={progress}
                    strokeWidth={5}
                    styles={buildStyles({
                      pathColor: theme.green,
                      trailColor: theme.lightGrey,
                    })}
                  />
                </AbsoluteFill>
                <Progress>{progress}%</Progress>
              </LogoWrapper>
            </Brand>
            {chapterRoutes.length > 1 && (
              <div style={{ position: 'relative' }}>
                <Chapters>
                  {chapterRoutes.map((chapterRoute, index) => {
                    return (
                      <Chapter key={chapterRoute.path}>
                        {chapterSaved &&
                        JSON.parse(chapterSaved) >= index + 1 &&
                        !isSkippedChapter(index + 1) ? (
                          <StyledLink
                            to={chapterRoute.path}
                            aria-label={`Stap ${index + 1} van ${chapterRoutes.length}`}
                          >
                            {index + 1}
                          </StyledLink>
                        ) : (
                          index + 1
                        )}
                        <Tooltip> {chapterRoute.title} </Tooltip>
                      </Chapter>
                    )
                  })}
                </Chapters>
                <ChapterIndicator $isActiveChapter={isActiveChapter} />
              </div>
            )}
          </LeftSide>

          <GoBackLink
            goBack={() => {
              history(-1)
            }}
          >
            Vorige
          </GoBackLink>
          <CloseAppButton
            onClick={onModalButtonClick}
            $inverted={routeIsIntroduction || modalIsOpen}
            disabled={modalIsOpen}
          />
        </StyledHeader>
      )}
    </>
  )
}

const StyledHeader = styled.header`
  position: absolute;
  top: var(--headerHeight);
  padding: 0 15px;
  font-size: 1rem;
  flex-shrink: 0;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 61px;
  z-index: 9;
  background-color: #fff;
  transition: transform 0.2s linear;

  @media (min-width: ${breakpoints.sm}) {
    padding: 0 20px;
  }

  @media (min-width: ${breakpoints.md}) {
    height: 85px;
  }

  &.scrolled {
    transform: translateY(calc(var(--headerHeight) * -1));
  }
`

const Background = styled.div`
  background: linear-gradient(to bottom, #f4f4f4, #fff 75%, rgba(255, 255, 255, 0) 100%);
  position: absolute;
  height: 65px;
  width: 100%;
  top: 61px;
  left: 0;

  @media (min-width: ${breakpoints.md}) {
    display: none;
  }
`

const LeftSide = styled.div`
  display: flex;
  align-items: center;
`

const Brand = styled.div`
  display: flex;
  text-align: center;
  margin-right: 10px;

  @media (min-width: ${breakpoints.md}) {
    margin-right: 25px;
  }
`

const LogoWrapper = styled.div`
  display: flex;
  position: relative;
  margin: 0 auto;
  width: 40px;
  height: 40px;
  text-align: center;
  justify-content: center;
  align-items: center;
`

const Progress = styled.span`
  color: ${({ theme }) => theme.warmGrey};
  font-weight: bold;
  font-size: 0.875rem;
  display: flex;
  align-items: center;

  @media (min-width: ${breakpoints.md}) {
    display: inline;
  }
`

const Chapters = styled.ul`
  display: flex;
  margin: 0 -15px 0 0;
  padding: 0;
  list-style: none;
`

const Tooltip = styled.span`
  display: none;
  background-color: white;
  border: 1px solid ${({ theme }) => theme.lightGrey};
  position: absolute;
  margin-top: 1rem;
  border-radius: 4px;
  box-shadow: 2px 2px 4px 1px rgba(105, 105, 105, 0.11);
  width: 11rem;
  font-size: 15px;
  color: ${({ theme }) => theme.warmGrey};
  font-weight: normal;
  line-height: 1;
  padding: 15px 7.5px;
  z-index: 100;
  left: 30px;
`

const Chapter = styled.li<{ theme: any }>`
  width: 30px;
  height: ${CHAPTER_HEIGHT}px;
  line-height: ${CHAPTER_HEIGHT}px;
  font-weight: bold;
  transition: opacity 600ms;
  display: block;
  text-align: center;
  cursor: default;
  position: relative;
  margin: 0 4px 2px 4px;
  color: ${({ theme }) => theme.mediumGrey};

  @media (min-width: ${breakpoints.md}) {
    &:hover ${Tooltip} {
      display: block;
    }
  }
`

const ChapterIndicator = styled.span<{ $isActiveChapter: number }>`
  display: block;
  height: 3px;
  width: 16px;
  background-color: ${({ theme }) => theme.yellow};
  position: absolute;
  bottom: 2px;
  left: 11px;
  transform: translateX(${({ $isActiveChapter }) => ($isActiveChapter - 1) * CHAPTER_WIDTH}px);
  transition: transform 600ms;
`

const StyledLink = styled(Link)`
  text-decoration: none;
  display: block;
  color: ${({ theme }) => theme.textColor};
  cursor: pointer;
`

export default Header
